import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Divider, Paper, Stack, TextField, Typography } from '@mui/material';
import api from '../api/client';
import { useTenant } from '../contexts/TenantContext';
import { usePermissions } from '../contexts/PermissionContext';
import { whatsappReplies, type ReplyConversation, type ReplyMessage } from './replyTransport.mjs';

function Panel({ organizationId, workspaceId, canSend }: { organizationId: string; workspaceId: string; canSend: boolean }) {
  const service = whatsappReplies(api, { organizationId, workspaceId });
  const alive = useRef(true);
  const selection = useRef(0);
  const [conversations, setConversations] = useState<ReplyConversation[]>([]);
  const [selected, setSelected] = useState<ReplyConversation | null>(null);
  const [messages, setMessages] = useState<ReplyMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    alive.current = true;
    void service.conversations().then(rows => { if (alive.current) setConversations(rows); })
      .catch(() => { if (alive.current) setError('WhatsApp conversations could not be loaded.'); });
    return () => { alive.current = false; };
  }, []);
  const open = async (conversation: ReplyConversation) => {
    const generation = ++selection.current;
    setSelected(conversation); setMessages([]); setError(''); setNotice('');
    try {
      const rows = await service.messages(conversation.id);
      if (alive.current && selection.current === generation) setMessages(rows);
    } catch { if (alive.current && selection.current === generation) setError('Messages could not be loaded.'); }
  };
  const send = async () => {
    if (!selected || busy) return;
    setBusy(true); setError(''); setNotice('');
    try {
      await service.send(selected, draft);
      if (!alive.current) return;
      setDraft('');
      await open(selected);
      if (alive.current) setNotice('Meta accepted the message. Delivery will be confirmed by a status update.');
    } catch {
      if (alive.current) setError('Send was not confirmed. Refresh the conversation before trying again; automatic retry is disabled. Check the account and the 24-hour reply window.');
    } finally { if (alive.current) setBusy(false); }
  };
  return <Paper variant="outlined" sx={{ p: 2, mb: 3 }}><Stack spacing={2}>
    <Typography variant="h6">WhatsApp replies</Typography>
    <Typography variant="body2">Ask your test recipient to message the connected business number first. Replies use the existing Meta connection within the 24-hour customer service window.</Typography>
    {error && <Alert severity="error">{error}</Alert>}
    {notice && <Alert severity="info">{notice}</Alert>}
    <Button disabled={busy} onClick={() => {
      void service.conversations().then(rows => { if (alive.current) setConversations(rows); })
        .catch(() => { if (alive.current) setError('WhatsApp conversations could not be loaded.'); });
      if (selected) void open(selected);
    }}>Refresh conversations and status</Button>
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <Stack sx={{ minWidth: 220, flex: 1 }} spacing={1}>
        {conversations.length === 0 && <Typography variant="body2">No WhatsApp conversations in this workspace yet.</Typography>}
        {conversations.map(c => <Button key={c.id} disabled={busy} variant={selected?.id === c.id ? 'contained' : 'outlined'}
          onClick={() => void open(c)}>{c.customerPhone}</Button>)}
      </Stack>
      <Stack sx={{ minWidth: 280, flex: 2 }} spacing={1}>
        {selected && <>
          <Typography variant="subtitle2">Conversation with {selected.customerPhone}</Typography>
          <Divider />
          {messages.slice().reverse().map(m => <Typography key={m.id} variant="body2">
            {m.direction === 'INBOUND' ? 'Customer' : 'Spark CRM'}: {m.body ?? '(non-text message)'} · {m.deliveryStatus}
          </Typography>)}
          {canSend && <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField fullWidth label="Reply" value={draft} onChange={e => setDraft(e.target.value)}
              inputProps={{ maxLength: 4096 }} disabled={busy} />
            <Button variant="contained" disabled={busy || !draft.trim()} onClick={() => void send()}>
              {busy ? <CircularProgress size={20} /> : 'Send'}
            </Button>
          </Box>}
        </>}
      </Stack>
    </Box>
  </Stack></Paper>;
}

export default function WhatsAppReplies() {
  const tenant = useTenant(); const permissions = usePermissions();
  if (tenant.loading || permissions.loading || !tenant.organizationId || !tenant.workspaceId
      || !permissions.hasPermission('COMM_HUB_VIEW')) return null;
  return <Panel key={JSON.stringify([tenant.organizationId, tenant.workspaceId])}
    organizationId={tenant.organizationId} workspaceId={tenant.workspaceId}
    canSend={permissions.hasPermission('COMM_WHATSAPP_SEND')} />;
}
